import { useEffect, useState } from "react";

import SectionPrompt from "../../Composites/SectionPrompt/SectionPrompt";
import useResizable from "@/hooks/useResizable";
import { appstr } from "@coreapp/utils/appstr";
import { CardWrapper } from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";
import { DialogTextContent, Divider, Flex, Text } from "@theorgchartdev/storybook";
import { GetStartingSection, RuleCriteriaTypeMapping } from "./ViewRuleEditor.utils";
import { useTopic } from "@/hooks/useTopic";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import { ViewRuleEditorContent } from "./ViewRuleEditorContent";
import { ViewRuleEditorFilter } from "./ViewRuleEditorFilter";
import { useRuleErrors } from "@/appComponents/Blocks/Composites/RuleInput/useRuleErrors";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { getContainerHeight, getContainerWidth } from "@/Utils/getContainerSize";

const ViewRuleEditor = () => {
  const {
    ruleContextValue: { theData, showRuleEditor, onCloseRuleEditor }
  } = useViewManagerContext();
  const { addToast } = useToast();

  let ruleData = theData;

  const [editedRule, setEditRule] = useState(null);
  const [initSection, setInitSection] = useState(null);
  const {
    errors: ruleErrors,
    validate: validateRule,
    reset: resetRuleErrors
  } = useRuleErrors(editedRule);

  useEffect(() => {
    if (!ruleData) {
      return;
    }

    setEditRule(ruleData.rule.Clone());
    resetRuleErrors();
    //make sure rule is fully set, if not set to blank default value
    if (editedRule?.GetRuleCriteriaType() === RuleCriteriaTypeMapping.Basic) {
      //set a blank rule if there are no rule pairs
      if (editedRule.GetNumRulePairs() === 0) {
        const rule = editedRule.Clone();
        rule.AddBlankRulePair();
        setEditRule(rule);
      } else {
        //check rules to see if values don't exist, if not set to blank default value
        for (var rule in editedRule.fRulePairList) {
          if (editedRule.fRulePairList[rule].fFieldName == undefined) {
            editedRule.fRulePairList[rule].fFieldName = "";
          }
          if (editedRule.fRulePairList[rule].fFieldClassName == undefined) {
            editedRule.fRulePairList[rule].fFieldClassName = "";
          }
          if (editedRule.fRulePairList[rule].fValue == undefined) {
            editedRule.fRulePairList[rule].fValue = "";
          }
          if (editedRule.fRulePairList[rule].fComparison == undefined) {
            editedRule.fRulePairList[rule].fComparison = "equals";
          }
        }
      }
    }

    setInitSection(GetStartingSection(ruleData.rule));
  }, [ruleData]);

  useEffect(() => {
    if (editedRule?.GetRuleCriteriaType() === RuleCriteriaTypeMapping.Basic) {
      if (editedRule.GetNumRulePairs() === 0) {
        const rule = editedRule.Clone();
        rule.AddBlankRulePair();
        setEditRule(rule);
      }
    }
  }, [editedRule]);

  useTopic("ModeSwitch", () => {
    onCloseRuleEditor();
  });

  const initialWidth = 600;
  const initialHeight = getContainerHeight() * 0.8;
  const minWidth = 400;
  const minHeight = getContainerHeight() * 0.7;

  const { size, handleMouseDown } = useResizable(initialWidth, initialHeight, minWidth, minHeight);
  const initOffset = {
    x: (getContainerWidth() - initialWidth) / 2,
    y: (getContainerHeight() - initialHeight) / 2
  };

  if (!editedRule || !ruleData || !showRuleEditor) {
    return;
  }

  const { index, view } = ruleData;
  const addNew = ruleData.addNew === true;
  const addOrEditStr = addNew ? appstr("Add") : appstr("Edit");
  const headerStr = addOrEditStr + " " + appstr("Rule") + " for " + view.GetTemplateName();

  const handleSave = () => {
    if (!validateRule()) return;
    const rule = editedRule.Clone();

    if (addNew) {
      view.AddRule(rule);
    } else {
      view.fRuleList[index] = rule;
    }

    ruleData.onClose(ruleData);
    onCloseRuleEditor();
    addToast({ message: appstr("View rule saved"), status: TOAST_STATUS.SUCCESS, timeoutMS: 5000 });
  };

  const footerProps = {
    footerActions: {
      cancelAction: () => onCloseRuleEditor(),
      customAction: {
        onClick: () => handleSave(),
        buttonProps: {
          dataTestId: "view-rule-editor-save-btn",
          modifier: "primary"
        },
        label: appstr("Save")
      }
    }
  };

  const handleFocusTrapPostDeactivate = () => {
    const node = document.querySelector("aside.sideContainer");
    node?.focus();
  };

  const handleSetFilterRule = newRule => {
    if (newRule?.GetRuleCriteriaType() !== editedRule?.GetRuleCriteriaType()) {
      resetRuleErrors();
    }
    setEditRule(newRule);
  };

  return (
    <CardWrapper
      show={showRuleEditor}
      prefixId={"modal"}
      onClose={onCloseRuleEditor}
      focusTrap={{
        returnFocusOnDeactivate: false,
        onPostDeactivate: handleFocusTrapPostDeactivate
      }}
      otherHeaderProps={{
        title: headerStr,
        icon: "flask-gear",
        iconProps: { color: "brand" }
      }}
      otherFooterProps={{ ...footerProps }}
      size={size}
      onMouseDownResize={handleMouseDown}
      initOffset={initOffset}
      minWidth={minWidth}
      minHeight={minHeight}
      otherOverlayProps={{ padding: 0 }}
      customClass={"oc-view-rule-card"}
    >
      <Flex
        dataTestId="view-rule-editor"
        direction={"column"}
        gap={"8px"}
        height={"100%"}
        overflow={"hidden"}
      >
        <div style={{ padding: "20px 24px" }}>
          <SectionPrompt
            title={"1. " + appstr("Which boxes should this rule apply to?")}
            description={appstr(
              "Choose which boxes (some boxes or all boxes) for this rule to target"
            )}
          >
            <DialogTextContent colorModifier="lightgrey">
              <ViewRuleEditorFilter
                rule={editedRule}
                setRule={handleSetFilterRule}
                errorsObject={ruleErrors}
              />
            </DialogTextContent>
          </SectionPrompt>
        </div>
        <Divider />
        <div style={{ height: "100%", padding: "0px 24px 20px 24px", overflow: "hidden" }}>
          <SectionPrompt
            title={"2. " + appstr("What should be done to those boxes?")}
            description={
              //TODO: replace with clarifcations in app today - see AddSubHeaderText
              appstr(
                "Set which formatting or style properties should apply to boxes that meet the criteria above"
              )
            }
            containerStyle={{ overflow: "hidden", height: "100%" }}
            contentStyle={{ overflow: "hidden", height: "100%" }}
          >
            <ViewRuleEditorContent
              initSection={initSection}
              rule={editedRule}
              setRule={setEditRule}
              onClose={onCloseRuleEditor}
              customWidth={size.width}
              minWidth={minWidth}
              currentTemplateId={view.GetTemplateID()}
            />
          </SectionPrompt>
        </div>
      </Flex>
    </CardWrapper>
  );
};

export default ViewRuleEditor;
